import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBooking } from '../../contexts/BookingContext';
import { generateTimeSlots, getTodayDate, formatDateShort, addDays, generate7DayRange, formatPrice } from '../../utils/timeUtils';
import { RichTextEditor } from '../RichTextEditor';
import { Calendar } from '../Calendar';
import { ConfirmationModal } from '../ConfirmationModal';
import { ReservationDetailModal } from '../ReservationDetailModal';
import { PaymentMethodModal } from './PaymentMethodModal';
import { RescheduleModal } from './RescheduleModal';
import type { Booking, DragSelection } from '../../types';
import type { AvailableTrainer } from '../../types/trainerAssociation';

// Znajduje realnie przewijalny kontener nad danym elementem. W tym portalu są dwa
// zagnieżdżone `overflow-auto` (MainLayout + siatka), a scrolluje ten, który ma
// nadmiar treści — dlatego wybieramy go dynamicznie, a nie na sztywno.
function findScrollableParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export const Scheduler7Day: React.FC = () => {
  const { t } = useTranslation();
  const { selectedFacility, fetchFacilityBookings, facilityTimeSlots, bookings, deleteBooking, getReservationSlots, cancelReservationSlots, toggleReservationPayment, applyProductToReservation, rescheduleReservation, updateReservationNotes } = useBooking();
  const [startDate, setStartDate] = useState(getTodayDate());
  const [showCalendar, setShowCalendar] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ bookingId: string; customerName: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [reservationDetailModal, setReservationDetailModal] = useState<string | null>(null);
  const [showBookingModal, setShowBookingModal] = useState<DragSelection | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ hour: number; date: string } | null>(null);
  const [dragEnd, setDragEnd] = useState<{ hour: number; date: string } | null>(null);
  const [paymentModal, setPaymentModal] = useState<{ reservationId: string; customerEmail: string } | null>(null);
  const [rescheduleModal, setRescheduleModal] = useState<{
    reservationId: string;
    currentDate: string;
    currentTimeSlots: string[];
    currentFacilityId: string;
    customerName: string;
  } | null>(null);

  const timeSlots = generateTimeSlots(6, 22);
  const dateRange = generate7DayRange(startDate);

  // Pierwsza otwarta godzina w skali tygodnia: najwcześniejszy wiersz, w którym
  // KTÓRYKOLWIEK z 7 dni jest otwarty (dostępny albo zajęty, byle nie „niedostępny").
  // Do niego przewijamy widok, żeby zaczynał się od godzin pracy obiektu, nie od 6:00.
  const firstOpenTime = timeSlots.find(time =>
    dateRange.some(date => {
      const daySlots = selectedFacility?.id ? facilityTimeSlots[selectedFacility.id]?.[date] || [] : [];
      const slot = daySlots.find(s => s.time === time);
      return !!slot && (slot.isAvailable || slot.isBooked);
    })
  ) ?? null;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const headerRowRef = useRef<HTMLDivElement>(null);
  const firstOpenRowRef = useRef<HTMLDivElement>(null);

  // Fetch bookings for all 7 days when facility or start date changes
  useEffect(() => {
    if (selectedFacility?.id && fetchFacilityBookings) {
      dateRange.forEach(date => {
        fetchFacilityBookings(selectedFacility.id, date).catch(err => {
          console.debug('Booking fetch completed with errors:', err);
        });
      });
    }
  }, [selectedFacility?.id, startDate]);

  // Domyślnie ustaw widok na pierwszą otwartą godzinę. Liczymy pozycję wiersza z
  // getBoundingClientRect i ustawiamy scrollTop NATYCHMIAST (bez smooth — pozycja
  // startowa). Odejmujemy wysokość sticky-nagłówka. Kilka prób w rAF, bo dane
  // slotów po odświeżeniu dochodzą asynchronicznie.
  useEffect(() => {
    if (!firstOpenTime) return;
    let cancelled = false;
    let attempts = 0;
    let rafId = 0;
    const tryScroll = () => {
      if (cancelled) return;
      const row = firstOpenRowRef.current;
      const preferred = scrollContainerRef.current;
      const scroller =
        preferred && preferred.scrollHeight > preferred.clientHeight + 1
          ? preferred
          : findScrollableParent(row);
      if (row && scroller) {
        const headerH = headerRowRef.current?.offsetHeight ?? 44;
        const delta = row.getBoundingClientRect().top - scroller.getBoundingClientRect().top - headerH;
        if (Math.abs(delta) > 2) scroller.scrollTop += delta;
      } else if (row) {
        // Nie znaleziono przewijalnego kontenera-diva — deleguj do przeglądarki,
        // która sama trafi we właściwy scrollport (także window). scroll-mt na
        // wierszu (className) chroni godzinę przed schowaniem pod sticky-nagłówkiem.
        row.scrollIntoView({ block: 'start', behavior: 'auto' });
      }
      attempts += 1;
      if (attempts < 10) rafId = requestAnimationFrame(tryScroll);
    };
    rafId = requestAnimationFrame(tryScroll);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [firstOpenTime, startDate, selectedFacility?.id]);

  const handlePrevious = () => {
    // Move back 7 days (show previous week starting from yesterday)
    setStartDate(addDays(startDate, -1));
  };

  const handleNext = () => {
    // Move forward 7 days (show next week starting from tomorrow)
    setStartDate(addDays(startDate, 1));
  };

  const handleToday = () => {
    setStartDate(getTodayDate());
  };

  const getSlotStatus = (date: string, time: string) => {
    if (!selectedFacility?.id) return null;
    const slots = facilityTimeSlots[selectedFacility.id]?.[date] || [];
    return slots.find(s => s.time === time);
  };

  const getBookingForSlot = (date: string, time: string): Booking | undefined => {
    if (!selectedFacility?.id) return undefined;
    return bookings.find((booking) => {
      if (booking.facilityId !== selectedFacility.id || booking.timeSlot.date !== date) {
        return false;
      }
      const startHour = parseInt(booking.timeSlot.startTime.split(':')[0]);
      const slotHour = parseInt(time.split(':')[0]);
      const endHour = parseInt(booking.timeSlot.endTime.split(':')[0]);
      return slotHour >= startHour && slotHour < endHour;
    });
  };

  const isBookingStart = (booking: Booking, time: string): boolean => {
    const startHour = parseInt(booking.timeSlot.startTime.split(':')[0]);
    const slotHour = parseInt(time.split(':')[0]);
    return slotHour === startHour;
  };

  const getBookingHeight = (booking: Booking): number => {
    const startHour = parseInt(booking.timeSlot.startTime.split(':')[0]);
    const endHour = parseInt(booking.timeSlot.endTime.split(':')[0]);
    const startMinutes = parseInt(booking.timeSlot.startTime.split(':')[1]);
    const endMinutes = parseInt(booking.timeSlot.endTime.split(':')[1]);
    return (endHour - startHour) + (endMinutes - startMinutes) / 60;
  };

  const handleSlotClick = (date: string, time: string) => {
    // Don't handle click if we just finished dragging
    if (isDragging) return;
    
    const slotStatus = getSlotStatus(date, time);
    const booking = getBookingForSlot(date, time);
    
    if (!booking && slotStatus?.isAvailable && !slotStatus.isBooked) {
      const hour = parseInt(time.split(':')[0]);
      setShowBookingModal({
        startHour: hour,
        endHour: hour + 1,
        date
      });
    }
  };

  const isToday = (date: string) => {
    return date === getTodayDate();
  };

  const handleMouseDown = (hour: number, date: string) => {
    const slotStatus = getSlotStatus(date, `${hour.toString().padStart(2, '0')}:00`);
    const booking = getBookingForSlot(date, `${hour.toString().padStart(2, '0')}:00`);
    
    // Only allow drag on available slots without bookings
    if (!booking && slotStatus?.isAvailable && !slotStatus.isBooked) {
      setIsDragging(true);
      setDragStart({ hour, date });
      setDragEnd({ hour, date });
    }
  };

  const handleMouseEnter = (hour: number, date: string) => {
    if (isDragging && dragStart && dragStart.date === date) {
      // Only allow dragging within the same date
      const slotStatus = getSlotStatus(date, `${hour.toString().padStart(2, '0')}:00`);
      const booking = getBookingForSlot(date, `${hour.toString().padStart(2, '0')}:00`);
      
      // Only extend drag to available slots
      if (!booking && slotStatus?.isAvailable && !slotStatus.isBooked) {
        setDragEnd({ hour, date });
      }
    }
  };

  const handleMouseUp = useCallback(() => {
    if (isDragging && dragStart && dragEnd && dragStart.date === dragEnd.date) {
      const startHour = Math.min(dragStart.hour, dragEnd.hour);
      const endHour = Math.max(dragStart.hour, dragEnd.hour) + 1;
      
      // Check if all slots in range are available
      const allSlotsAvailable = timeSlots
        .filter(slot => {
          const hour = parseInt(slot.split(':')[0]);
          return hour >= startHour && hour < endHour;
        })
        .every(slot => {
          const slotStatus = getSlotStatus(dragStart.date, slot);
          const booking = getBookingForSlot(dragStart.date, slot);
          return !booking && slotStatus?.isAvailable && !slotStatus.isBooked;
        });

      if (allSlotsAvailable) {
        setShowBookingModal({
          startHour,
          endHour,
          date: dragStart.date
        });
      }
    }
    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
  }, [isDragging, dragStart, dragEnd, timeSlots]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseUp]);

  const isSlotInDragSelection = (hour: number, date: string): boolean => {
    if (!isDragging || !dragStart || !dragEnd || dragStart.date !== date) {
      return false;
    }
    const startHour = Math.min(dragStart.hour, dragEnd.hour);
    const endHour = Math.max(dragStart.hour, dragEnd.hour);
    return hour >= startHour && hour <= endHour;
  };

  if (!selectedFacility) {
    return (
      <div className="flex-1 flex items-center justify-center bg-surface-app">
        <div className="text-center">
          <div className="text-6xl mb-4">📅</div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-ink-primary mb-2">{t('scheduler.noFacilitySelected')}</h2>
          <p className="text-ink-secondary">{t('scheduler.selectFacilityMessage')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-surface-app flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="bg-surface-card border-b border-border-subtle px-6 pt-4 pb-3">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-ink-secondary">
            {formatDateShort(dateRange[0])} - {formatDateShort(dateRange[6])}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevious}
              className="w-8 h-8 flex items-center justify-center bg-surface-card border border-border-strong text-ink-primary rounded-lg hover:bg-surface-card-hover transition-colors"
              aria-label={t('scheduler.previous')}
              title={t('scheduler.previous')}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={handleToday}
              className="px-3 py-1.5 text-sm bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors"
            >
              {t('scheduler.today')}
            </button>
            <button
              onClick={() => setShowCalendar(true)}
              className="w-8 h-8 flex items-center justify-center bg-surface-card border border-border-strong text-ink-primary rounded-lg hover:bg-surface-card-hover transition-colors"
              aria-label={t('scheduler.calendar')}
              title={t('buttons.chooseDateFromCalendar')}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
            <button
              onClick={handleNext}
              className="w-8 h-8 flex items-center justify-center bg-surface-card border border-border-strong text-ink-primary rounded-lg hover:bg-surface-card-hover transition-colors"
              aria-label={t('scheduler.next')}
              title={t('scheduler.next')}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Scheduler Grid */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto">
        <div className="min-w-max">
          {/* Day Headers */}
          <div ref={headerRowRef} className="sticky top-0 z-20 bg-surface-card border-b-2 border-border-strong">
            <div className="flex">
              <div className="w-20 flex-shrink-0 border-r border-border-subtle bg-surface-card"></div>
              {dateRange.map((date) => (
                <div
                  key={date}
                  className={`flex-1 min-w-[140px] p-4 text-center border-r border-border-subtle ${
                    isToday(date) ? 'bg-brand-primary/10' : ''
                  }`}
                >
                  <div className={`font-semibold ${isToday(date) ? 'text-brand-primary' : 'text-ink-primary'}`}>
                    {formatDateShort(date)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Time Slots Grid */}
          <div>
            {timeSlots.map((time) => (
              <div
                key={time}
                ref={time === firstOpenTime ? firstOpenRowRef : null}
                className="flex border-b border-border-subtle"
              >
                {/* Time Label */}
                <div className="w-20 flex-shrink-0 flex items-center justify-center border-r border-border-subtle bg-surface-app-elevated py-4">
                  <span className="text-sm font-medium text-ink-secondary">{time}</span>
                </div>

                {/* Day Columns */}
                {dateRange.map((date) => {
                  const slotStatus = getSlotStatus(date, time);
                  const booking = getBookingForSlot(date, time);
                  const shouldShowBooking = booking && isBookingStart(booking, time);

                  let slotBgClass = 'bg-surface-card';
                  let slotBorderClass = '';
                  let slotTextClass = 'text-ink-muted';
                  let slotStatusText = '';
                  let cursorClass = '';

                  // Check if slot has business association
                  const hasBusinessAssociation = !!slotStatus?.businessProfileId;

                  if (slotStatus) {
                    if (slotStatus.isBooked) {
                      slotBgClass = 'bg-surface-app-elevated';
                      slotTextClass = 'text-ink-muted';
                      slotStatusText = '';
                    } else if (hasBusinessAssociation) {
                      // Trainer slot — slot assigned to a trainer running their own classes
                      slotBgClass = 'bg-brand-primary-light/40 dark:bg-brand-primary-light/55';
                      slotTextClass = 'text-brand-primary-deep dark:text-white';
                      slotStatusText = slotStatus.businessProfileName || t('legend.businessSlot');
                      slotBorderClass = 'border-l-2 border-brand-primary-light';
                    } else if (slotStatus.isAvailable) {
                      slotBgClass = 'bg-brand-confirm/15 dark:bg-brand-confirm/30';
                      slotTextClass = 'text-brand-confirm-deep dark:text-brand-confirm';
                      slotStatusText = t('legend.available');
                      if (!booking) {
                        slotBgClass += ' hover:bg-brand-confirm/25 dark:hover:bg-brand-confirm/45';
                        cursorClass = 'cursor-pointer';
                      }
                    } else {
                      slotBgClass = 'bg-surface-app-elevated';
                      slotTextClass = 'text-ink-muted';
                      slotStatusText = t('legend.notAvailable');
                    }
                  }

                  if (isToday(date)) {
                    slotBorderClass = 'border-l-2 border-r-2 border-brand-primary/40';
                  }

                  const hour = parseInt(time.split(':')[0]);
                  const isDragSelected = isSlotInDragSelection(hour, date);

                  // Add drag selection highlight
                  if (isDragSelected) {
                    slotBgClass += ' bg-brand-primary/15 dark:bg-brand-primary/30 ring-2 ring-brand-primary ring-inset';
                  }

                  return (
                    <div
                      key={`${date}-${time}`}
                      className={`flex-1 min-w-[140px] min-h-[60px] p-2 border-r border-border-subtle ${slotBgClass} ${slotBorderClass} ${cursorClass} transition-colors relative select-none`}
                      onClick={() => handleSlotClick(date, time)}
                      onMouseDown={() => handleMouseDown(hour, date)}
                      onMouseEnter={() => handleMouseEnter(hour, date)}
                    >
                      {shouldShowBooking && booking && (
                        <div
                          className={`absolute inset-2 rounded-lg p-2 shadow-lg overflow-hidden cursor-pointer ${
                            booking.isPaid
                              ? 'bg-brand-confirm hover:brightness-110'
                              : 'bg-brand-accent hover:brightness-110'
                          }`}
                          style={{
                            height: `${getBookingHeight(booking) * 60 - 8}px`,
                            zIndex: 10,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const lastHyphenIndex = booking.id.lastIndexOf('-');
                            const reservationId = lastHyphenIndex !== -1 ? booking.id.substring(0, lastHyphenIndex) : booking.id;
                            setReservationDetailModal(reservationId);
                          }}
                        >
                          {booking.groupId && (
                            <div className="absolute top-0 left-0 right-0 h-[3px] bg-brand-primary-light" aria-hidden />
                          )}
                          <div className="flex flex-col h-full">
                            {/* Action buttons row — top */}
                            <div className="flex flex-shrink-0 justify-between items-center">
                              <div className="flex gap-0.5 items-center">
                                {!booking.paymentId && !booking.isPaid && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const lastHyphenIndex = booking.id.lastIndexOf('-');
                                      const reservationId = lastHyphenIndex !== -1 ? booking.id.substring(0, lastHyphenIndex) : booking.id;
                                      setPaymentModal({
                                        reservationId,
                                        customerEmail: booking.customerEmail || '',
                                      });
                                    }}
                                    className="px-1.5 py-0.5 bg-white/20 hover:bg-white/30 rounded text-xs text-white transition-colors"
                                    title={t('buttons.markAsPaid')}
                                  >
                                    $
                                  </button>
                                )}
                                {booking.paymentId && (
                                  <span className="px-1.5 py-0.5 bg-white/25 rounded text-xs text-white" title={t('buttons.markAsPaid')}>
                                    💳
                                  </span>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const lastHyphenIndex = booking.id.lastIndexOf('-');
                                    const reservationId = lastHyphenIndex !== -1 ? booking.id.substring(0, lastHyphenIndex) : booking.id;
                                    const startHour = parseInt(booking.timeSlot.startTime.split(':')[0]);
                                    const endHour = parseInt(booking.timeSlot.endTime.split(':')[0]);
                                    const slots: string[] = [];
                                    for (let h = startHour; h < endHour; h++) {
                                      slots.push(`${h.toString().padStart(2, '0')}:00`);
                                    }
                                    setRescheduleModal({
                                      reservationId,
                                      currentDate: booking.timeSlot.date,
                                      currentTimeSlots: slots,
                                      currentFacilityId: booking.facilityId,
                                      customerName: booking.customerName,
                                    });
                                  }}
                                  className="px-1.5 py-0.5 bg-white/25 hover:bg-white/40 rounded text-xs text-white transition-colors"
                                  title={t('buttons.reschedule') || 'Reschedule'}
                                >
                                  R
                                </button>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConfirmation({
                                    bookingId: booking.id,
                                    customerName: booking.customerName,
                                  });
                                }}
                                className="px-1.5 py-0.5 bg-white/25 hover:bg-white/40 rounded text-xs text-white transition-colors"
                                title={t('buttons.deleteBooking')}
                              >
                                ×
                              </button>
                            </div>
                            {/* Customer info — below buttons */}
                            <div className="font-semibold text-white text-xs truncate flex items-center gap-1 mt-1">
                              {booking.groupId && (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3 flex-shrink-0 text-white/90" aria-label="Group booking">
                                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                  <circle cx="9" cy="7" r="4" />
                                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                              )}
                              <span className="truncate">{booking.customerName}</span>
                            </div>
                            {booking.customerPhone && getBookingHeight(booking) > 1.5 && (
                              <div className="text-xs text-white/75 mt-1 truncate">
                                {booking.customerPhone}
                              </div>
                            )}
                            {booking.notes && getBookingHeight(booking) > 2 && (
                              <div className="text-xs text-white/75 mt-1 border-t border-white/20 pt-1 truncate">
                                {booking.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {!shouldShowBooking && slotStatusText && (
                        <div className={`text-xs font-medium ${slotTextClass} truncate`}>
                          {slotStatusText}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-surface-card border-t border-border-subtle p-4">
        <div className="flex flex-wrap items-center gap-6 text-sm">
          <div className="font-semibold text-ink-primary">{t('legend.slotStatus')}</div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-brand-confirm/15 dark:bg-brand-confirm/30"></div>
            <span className="text-ink-secondary">{t('legend.available')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-surface-app-elevated"></div>
            <span className="text-ink-secondary">{t('legend.notAvailable')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-brand-primary-light/40 dark:bg-brand-primary-light/55 border-l-2 border-brand-primary-light"></div>
            <span className="text-ink-secondary">{t('legend.businessSlot')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-brand-confirm"></div>
            <span className="text-ink-secondary">{t('legend.paid')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-brand-accent"></div>
            <span className="text-ink-secondary">{t('legend.unpaid')}</span>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <BookingModalPlaceholder
          facilityId={selectedFacility.id}
          facilityPricePerHour={selectedFacility.grossPricePerHour ?? selectedFacility.pricePerHour}
          dragSelection={showBookingModal}
          onClose={() => setShowBookingModal(null)}
        />
      )}

      {/* Cancel Booking Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirmation !== null}
        title={t('confirmation.cancelBooking')}
        message={`${t('confirmation.confirmCancelBookingMessage')} ${deleteConfirmation?.customerName}?`}
        errorMessage={cancelError}
        confirmLabel={isDeleting ? t('confirmation.cancelling') : t('confirmation.cancelBookingButton')}
        cancelLabel={t('booking.close')}
        confirmVariant="danger"
        showNotesField={true}
        notesLabel={t('confirmation.cancellationReason')}
        notesPlaceholder={t('confirmation.cancellationPlaceholder')}
        onConfirm={async (notes) => {
          if (deleteConfirmation && !isDeleting) {
            setIsDeleting(true);
            setCancelError(null);
            try {
              await deleteBooking(deleteConfirmation.bookingId, notes);
              setDeleteConfirmation(null);
              setCancelError(null);
            } catch (err: any) {
              console.error('Failed to cancel booking:', err);
              let errorMessage = err?.data?.message || err?.message || t('booking.createBookingError');
              
              // Handle specific API error messages with proper translations
              if (errorMessage.includes('Cannot cancel same-day or past reservations')) {
                errorMessage = t('errors.cannotCancelSameDayReservations');
              } else if (errorMessage.includes('not allowed') || errorMessage.includes('nie jest dozwolone')) {
                errorMessage = t('errors.cancellationNotAllowed');
              } else if (!errorMessage.startsWith(t('errors.'))) {
                // If it's not a translated error, use general error
                errorMessage = t('errors.generalError');
              }
              
              setCancelError(errorMessage);
            } finally {
              setIsDeleting(false);
            }
          }
        }}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteConfirmation(null);
            setCancelError(null);
          }
        }}
      />

      {/* Reservation Detail Modal */}
      <ReservationDetailModal
        isOpen={reservationDetailModal !== null}
        reservationId={reservationDetailModal}
        onClose={() => setReservationDetailModal(null)}
        onCancelComplete={() => {
          if (selectedFacility?.id) {
            dateRange.forEach(date => {
              fetchFacilityBookings(selectedFacility.id, date);
            });
          }
        }}
        onFetchReservation={getReservationSlots}
        onCancelSlots={cancelReservationSlots}
        onTogglePayment={toggleReservationPayment}
        onUpdateNotes={updateReservationNotes}
        onReschedule={(reservation) => {
          const activeSlots = reservation.slots
            .filter(s => s.status === 'Active')
            .map(s => s.timeSlot);
          setReservationDetailModal(null);
          setRescheduleModal({
            reservationId: reservation.id,
            currentDate: reservation.date.substring(0, 10),
            currentTimeSlots: activeSlots,
            currentFacilityId: reservation.facilityId,
            customerName: reservation.customerName || '',
          });
        }}
      />

      {/* Calendar Modal */}
      <Calendar
        isOpen={showCalendar}
        selectedDate={startDate}
        onDateSelect={(date) => {
          setStartDate(date);
          setShowCalendar(false);
        }}
        onClose={() => setShowCalendar(false)}
      />

      {/* Payment Method Modal */}
      <PaymentMethodModal
        isOpen={paymentModal !== null}
        reservationId={paymentModal?.reservationId || ''}
        customerEmail={paymentModal?.customerEmail || ''}
        onClose={() => setPaymentModal(null)}
        onPayWithCash={async (reservationId) => {
          await toggleReservationPayment(reservationId);
          setPaymentModal(null);
          // Refresh all visible dates to ensure color updates
          if (selectedFacility?.id) {
            dateRange.forEach(date => {
              fetchFacilityBookings(selectedFacility.id, date);
            });
          }
        }}
        onPayWithProduct={async (reservationId, purchaseIdPrefix, userEmail) => {
          await applyProductToReservation(reservationId, { purchaseIdPrefix, userEmail });
          setPaymentModal(null);
          // Refresh all visible dates to ensure color updates
          if (selectedFacility?.id) {
            dateRange.forEach(date => {
              fetchFacilityBookings(selectedFacility.id, date);
            });
          }
        }}
      />

      {/* Reschedule Modal */}
      <RescheduleModal
        isOpen={rescheduleModal !== null}
        reservationId={rescheduleModal?.reservationId || ''}
        currentDate={rescheduleModal?.currentDate || ''}
        currentTimeSlots={rescheduleModal?.currentTimeSlots || []}
        currentFacilityId={rescheduleModal?.currentFacilityId || ''}
        customerName={rescheduleModal?.customerName || ''}
        onClose={() => setRescheduleModal(null)}
        onReschedule={async (reservationId, data) => {
          await rescheduleReservation(reservationId, data);
          setRescheduleModal(null);
        }}
      />
    </div>
  );
};

// Booking Modal Component
const BookingModalPlaceholder: React.FC<{
  facilityId: string;
  facilityPricePerHour?: number;
  dragSelection: DragSelection;
  onClose: () => void;
}> = ({ facilityId, facilityPricePerHour, dragSelection, onClose }) => {
  const { t } = useTranslation();
  const { createBooking, isTimeSlotBooked, fetchAvailableTrainers } = useBooking();
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');

  // Allow editing of time slots
  const [startHour, setStartHour] = useState(dragSelection.startHour);
  const [endHour, setEndHour] = useState(dragSelection.endHour);
  const duration = endHour - startHour;
  const [customPrice, setCustomPrice] = useState('');
  const [hasManualPrice, setHasManualPrice] = useState(false);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Trainer state
  const [availableTrainers, setAvailableTrainers] = useState<AvailableTrainer[]>([]);
  const [loadingTrainers, setLoadingTrainers] = useState(false);
  const [selectedTrainerId, setSelectedTrainerId] = useState<string | null>(null);

  const date = dragSelection.date;

  // Get selected trainer's price
  const selectedTrainer = availableTrainers.find((t: any) =>
    (t.trainerProfileId || t.id) === selectedTrainerId
  ) as any;
  const trainerPricePerHour = selectedTrainer?.grossHourlyRate || selectedTrainer?.hourlyRate || selectedTrainer?.pricePerHour || 0;
  const facilityPrice = (facilityPricePerHour || 0) * duration;
  const trainerPrice = trainerPricePerHour * duration;
  const calculatedPrice = facilityPrice + trainerPrice;

  // Update custom price when calculated price changes (but only if not manually set)
  useEffect(() => {
    if (!hasManualPrice) {
      setCustomPrice(calculatedPrice > 0 ? formatPrice(calculatedPrice) : '');
    }
  }, [calculatedPrice, hasManualPrice]);

  // Fetch available trainers when time changes
  useEffect(() => {
    const fetchTrainers = async () => {
      if (startHour >= endHour) return;

      const timeSlotsToCheck: string[] = [];
      for (let hour = startHour; hour < endHour; hour++) {
        timeSlotsToCheck.push(`${hour.toString().padStart(2, '0')}:00`);
      }

      setLoadingTrainers(true);
      try {
        const trainers = await fetchAvailableTrainers(date, timeSlotsToCheck);
        setAvailableTrainers(trainers);
        // Reset trainer selection if currently selected trainer is no longer available
        if (selectedTrainerId && !trainers.find((t: any) => (t.trainerProfileId || t.id) === selectedTrainerId)) {
          setSelectedTrainerId(null);
        }
      } catch (err) {
        console.error('Failed to fetch trainers:', err);
        setAvailableTrainers([]);
      } finally {
        setLoadingTrainers(false);
      }
    };

    fetchTrainers();
  }, [date, startHour, endHour, fetchAvailableTrainers]);

  // Check availability for selected time range
  const checkSlotAvailability = useCallback((hour: number) => {
    const timeSlot = {
      startTime: `${hour.toString().padStart(2, '0')}:00`,
      endTime: `${(hour + 1).toString().padStart(2, '0')}:00`,
      date
    };
    return !isTimeSlotBooked(facilityId, timeSlot);
  }, [facilityId, date, isTimeSlotBooked]);

  // Get available hours for dropdowns
  const availableHours = Array.from({ length: 24 }, (_, i) => i).filter(hour => {
    return hour >= 6 && hour <= 23; // Basic business hours
  });

  // Validate if selected time range is available
  const isRangeAvailable = useMemo(() => {
    if (startHour >= endHour) return false;

    for (let hour = startHour; hour < endHour; hour++) {
      if (!checkSlotAvailability(hour)) {
        return false;
      }
    }
    return true;
  }, [startHour, endHour, checkSlotAvailability]);

  const [isInitialMount, setIsInitialMount] = useState(true);

  useEffect(() => {
    setIsInitialMount(false);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerName) {
      setError(t('booking.enterCustomerNameError'));
      return;
    }

    if (!isRangeAvailable) {
      setError(t('booking.timeSlotNotAvailable'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await createBooking({
        facilityId,
        timeSlot: {
          startTime: `${startHour.toString().padStart(2, '0')}:00`,
          endTime: `${endHour.toString().padStart(2, '0')}:00`,
          date,
        },
        customerName,
        customerPhone,
        customerEmail,
        isPaid: false,
        notes,
        customPrice: customPrice ? parseFloat(customPrice) : undefined,
        trainerProfileId: selectedTrainerId,
      });

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('booking.createBookingError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-brand-primary-deep rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="font-display text-xl font-semibold tracking-tight text-white mb-4">{t('booking.createBooking')}</h3>

        <div className="mb-4 p-3 bg-surface-card rounded-lg">
          <div className="text-sm text-ink-secondary mb-3">{t('booking.timeSlot')}</div>

          {/* Time Slot Editing */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs text-ink-secondary mb-1">{t('booking.startTime')}</label>
              <select
                value={startHour}
                onChange={(e) => {
                  const newStart = parseInt(e.target.value);
                  setStartHour(newStart);
                  if (newStart >= endHour) {
                    setEndHour(newStart + 1);
                  }
                }}
                className="w-full px-2 py-1 bg-surface-card border border-border-strong rounded text-ink-primary text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary"
              >
                {availableHours.map(hour => (
                  <option key={hour} value={hour}>
                    {hour.toString().padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-ink-secondary mb-1">{t('booking.endTime')}</label>
              <select
                value={endHour}
                onChange={(e) => setEndHour(parseInt(e.target.value))}
                className="w-full px-2 py-1 bg-surface-card border border-border-strong rounded text-ink-primary text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary"
              >
                {availableHours.filter(hour => hour > startHour).map(hour => (
                  <option key={hour} value={hour}>
                    {hour.toString().padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Current Selection Display */}
          <div className="text-ink-primary font-semibold">
            {startHour.toString().padStart(2, '0')}:00 - {endHour.toString().padStart(2, '0')}:00
            <span className="text-ink-secondary text-sm ml-2">({duration}h)</span>
          </div>
          <div className="text-sm text-ink-secondary mt-1">{formatDateShort(date)}</div>

          {/* Availability Warning */}
          {!isInitialMount && !isRangeAvailable && (
            <div className="mt-2 p-2 bg-brand-warm/20 border border-brand-warm/40 rounded">
              <p className="text-xs text-brand-warm">{t('booking.timeSlotNotAvailable')}</p>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-brand-warm/20 border border-brand-warm/40 rounded-lg">
            <p className="text-sm text-brand-warm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              {t('booking.customerNameRequired')}
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              placeholder={t('booking.customerNamePlaceholder')}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              {t('booking.phoneNumber')}
            </label>
            <input
              type="tel"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              placeholder={t('booking.phoneNumberPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              {t('booking.email')}
            </label>
            <input
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              placeholder={t('booking.emailPlaceholder')}
            />
          </div>

          {/* Trainer Selection */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              {t('groupBooking.trainer')}
            </label>
            <select
              value={selectedTrainerId || ''}
              onChange={(e) => setSelectedTrainerId(e.target.value || null)}
              disabled={loadingTrainers}
              className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:opacity-50"
            >
              <option value="">{t('groupBooking.noTrainer')}</option>
              {loadingTrainers ? (
                <option disabled>{t('groupBooking.loadingTrainers')}</option>
              ) : (
                availableTrainers.map((trainer: any) => {
                  const trainerPrice = trainer.grossHourlyRate || trainer.hourlyRate || trainer.pricePerHour;
                  return (
                    <option key={trainer.trainerProfileId || trainer.id} value={trainer.trainerProfileId || trainer.id}>
                      {trainer.trainerDisplayName || trainer.displayName || trainer.name}
                      {trainerPrice ? ` (+${formatPrice(trainerPrice)} PLN/h)` : ''}
                    </option>
                  );
                })
              )}
            </select>
            {!loadingTrainers && availableTrainers.length === 0 && (
              <p className="text-xs text-ink-muted mt-1">{t('groupBooking.noTrainersAvailable')}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              {t('booking.price')}
              {trainerPrice > 0 && (
                <span className="text-xs text-ink-muted ml-1">({t('groupBooking.includesTrainer')})</span>
              )}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={customPrice}
              onChange={(e) => {
                setCustomPrice(e.target.value);
                setHasManualPrice(true);
              }}
              className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              placeholder={calculatedPrice.toString()}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              {t('booking.notes')}
            </label>
            <RichTextEditor
              value={notes}
              onChange={setNotes}
              placeholder={t('booking.notesPlaceholder')}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('booking.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('booking.creating') : t('booking.createButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
